import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  Header,
  Button,
  Icon,
  Popup,
  Table,
  Segment,
  Loader,
  Label,
  Grid,
  Pagination,
  Container,
  Card,
} from 'semantic-ui-react';
import moment from 'moment/moment';

import { PAGE_SIZE, formatDate, sumArray } from '../../constants';
import { useGetEvaluationSessionsQuery } from './evaluationSessionApiSlice';
import EvaluationTabbedResult from './EvaluationTabbedResult';
import './EvaluationStyle.css';
import '../../components/SharedCard.css';

const EvaluationSessionsList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // get query params from frontend urlpath
  const queryParams = new URLSearchParams(location.search);
  const page = queryParams.get('page');
  const page_size = queryParams.get('page_size');
  const chat_session = queryParams.get('chat_session');
  const trainee = queryParams.get('trainee');
  const partner = queryParams.get('partner');

  // remove null ones to pass to backend
  let qp = { page, page_size, chat_session, trainee, partner };
  qp = Object.entries(qp)
    .filter(e => e[1])
    .reduce((accumulator, [k, v]) => {
      accumulator[k] = v;
      return accumulator;
    }, {});

  // GET list
  const { data, isLoading, isSuccess } = useGetEvaluationSessionsQuery(
    {
      ...qp,
    },
    {
      pollingInterval: 5000,
    }
  );

  // remove postprocessing sessions
  const [evaluation_sessions, set_evaluation_sessions] = useState([]);
  const withinOneMonth = date => {
    const ctr_date = moment().subtract(30, 'days');
    const given_date = moment(date);
    return given_date.isAfter(ctr_date);
  };
  useEffect(() => {
    if (data) {
      set_evaluation_sessions(
        data.results.filter(
          es => es.is_postprocessed || withinOneMonth(es.created_at)
        )
      );
    }
  }, [data]);

  // handle select evaluation session
  const [selectedEvaluationSession, setSelectedEvaluationSession] =
    useState(null);
  const handleSelectEvaluationSession = evaluation_session => {
    setSelectedEvaluationSession(evaluation_session);
  };

  // handle pagination change
  const handlePaginationChange = (e, { activePage }) => {
    setSelectedEvaluationSession(null);
    let url = `/evaluationsessionslist?page=${activePage}&page_size=${PAGE_SIZE}`;
    if (chat_session) {
      url = url + `&chat_session=${chat_session}`;
    }
    navigate(url);
  };

  let content;
  if (isLoading) {
    content = <Loader active />;
  } else if (isSuccess) {
    content = (
      <div
        style={{
          // backgroundColor: '#f9fafb',
          // minHeight: '100vh',
          height: 'auto',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header Section */}
        <div
          style={{
            flex: '0 0 auto',
            padding: '16px 0',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(0,0,0,0.05)',
          }}
        >
          <Container fluid>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '0 20px',
              }}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#F2F2F7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon
                  name="file alternate outline"
                  style={{ margin: 0, color: '#007AFF', fontSize: '18px' }}
                />
              </div>
              <div>
                <h2
                  style={{
                    fontSize: '17px',
                    fontWeight: '600',
                    margin: 0,
                    color: '#1d1d1f',
                  }}
                >
                  AI 평가
                </h2>
                <p style={{ fontSize: '13px', color: '#86868b', margin: 0 }}>
                  View evaluation results
                </p>
              </div>
            </div>
          </Container>
        </div>

        <Container
          fluid
          style={{
            padding: '20px',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {isMobile ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: '16px',
              }}
            >
              {evaluation_sessions.map(evaluation_session => (
                <div
                  key={evaluation_session.id}
                  className="custom-card"
                  onClick={() => handleSelectEvaluationSession(evaluation_session)}
                >
                  <div className="custom-card-status-container">
                    {evaluation_session.is_postprocessed ? (
                      <Label
                        color={
                          evaluation_session.total_score >= 80
                            ? 'green'
                            : evaluation_session.total_score >= 50
                            ? 'yellow'
                            : 'red'
                        }
                        basic
                        size="mini"
                        circular
                      >
                        {evaluation_session.total_score.toFixed(0)}
                      </Label>
                    ) : (
                      <Label color="yellow" basic size="mini" circular>
                        <Icon name="spinner" loading />
                      </Label>
                    )}
                  </div>

                  <div className="custom-card-header">
                    <h3 className="custom-card-title">
                      {formatDate(evaluation_session.created_at)}
                    </h3>
                    <Label
                      circular
                      color={
                        selectedEvaluationSession &&
                        selectedEvaluationSession.id === evaluation_session.id
                          ? 'blue'
                          : undefined
                      }
                      size="mini"
                      style={{ marginLeft: '8px' }}
                    >
                      {evaluation_session.id.slice(0, 5)}
                    </Label>
                  </div>

                  <div className="custom-card-description">
                    <div style={{ marginBottom: '0.5em' }}>
                      <Icon
                        name={
                          evaluation_session.chat_session.persona?.gender ===
                          'MALE'
                            ? 'man'
                            : evaluation_session.chat_session.persona
                                ?.gender === 'FEMALE'
                            ? 'woman'
                            : 'user'
                        }
                        style={{ marginRight: '4px' }}
                      />
                      <strong>Persona: </strong>{' '}
                      {evaluation_session.chat_session.persona?.name ||
                        '실제 인물'}
                    </div>
                    <div>
                      <Icon name="user circle" style={{ marginRight: '4px' }} />
                      <strong>Trainee: </strong>{' '}
                      {evaluation_session.trainee?.full_name ||
                        evaluation_session.trainee?.username ||
                        '-'}
                    </div>
                    <div>
                      <Icon name="building outline" style={{ marginRight: '4px' }} />
                      <strong>Contractor: </strong>{' '}
                      {evaluation_session.trainee?.contractor?.name || '-'}
                    </div>
                  </div>

                  <div className="custom-card-actions">
                    <Button
                      fluid
                      size="small"
                      className="custom-card-btn-secondary"
                      icon="comments"
                      content="채팅"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(
                          `/chatsessionperform?chatsession_id=${evaluation_session.chat_session.id}`
                        );
                      }}
                    />
                    <Button
                      fluid
                      size="small"
                      className="custom-card-btn-primary"
                      icon="file text"
                      content="보고서"
                      disabled={!evaluation_session.is_postprocessed}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(
                          `/evaluationsessiondetail?evaluation_session=${evaluation_session.id}`
                        );
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Grid style={{  margin: 0 }} container={false}>
              {/* Left Pane: Table */}
              <Grid.Column width={8} style={{ maxHeight: '100%', paddingBottom: 0 }}>
                <div
                  style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    border: '1px solid rgba(0,0,0,0.05)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    overflowY: 'auto',
                    // maxHeight: '100%',
                  }}
                >
                  <div>
                    <Table
                      compact="very"

                      selectable
                      style={{ margin: 0, border: 'none' }}
                    >
                      <Table.Header style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'white' }}>
                        <Table.Row>
                          <Table.HeaderCell style={{ borderBottom: '1px solid rgba(34,36,38,.15)' }}>ID</Table.HeaderCell>
                          <Table.HeaderCell style={{ borderBottom: '1px solid rgba(34,36,38,.15)' }}>일시</Table.HeaderCell>
                          <Table.HeaderCell style={{ borderBottom: '1px solid rgba(34,36,38,.15)' }}>페르소나</Table.HeaderCell>
                          <Table.HeaderCell style={{ borderBottom: '1px solid rgba(34,36,38,.15)' }}>직원</Table.HeaderCell>
                          <Table.HeaderCell style={{ borderBottom: '1px solid rgba(34,36,38,.15)' }}>소속</Table.HeaderCell>
                          <Table.HeaderCell style={{ borderBottom: '1px solid rgba(34,36,38,.15)' }}>점수</Table.HeaderCell>
                          <Table.HeaderCell style={{ borderBottom: '1px solid rgba(34,36,38,.15)' }}>상태</Table.HeaderCell>
                          <Table.HeaderCell style={{ borderBottom: '1px solid rgba(34,36,38,.15)' }}>액션</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>

                    <Table.Body>
                      {evaluation_sessions.map((evaluation_session, i) => (
                        <Table.Row
                          key={evaluation_session.id}
                          active={
                            selectedEvaluationSession &&
                            selectedEvaluationSession.id === evaluation_session.id
                          }
                          onClick={() =>
                            handleSelectEvaluationSession(evaluation_session)
                          }
                          style={{ cursor: 'pointer' }}
                        >
                          <Table.Cell>
                            <Label
                              circular
                              color={
                                selectedEvaluationSession &&
                                selectedEvaluationSession.id ===
                                  evaluation_session.id
                                  ? 'blue'
                                  : undefined
                              }
                              onClick={(e) => {
                              // e.stopPropagation();
                              navigator.clipboard.writeText('evaluation session id:' + evaluation_session.id);
                            }}
                            style={{ cursor: 'pointer' }}
                            title="Click to copy ID"
                            >
                              {evaluation_session.id.slice(0, 5)}
                            </Label>
                          </Table.Cell>

                          <Table.Cell>
                            {formatDate(evaluation_session.created_at)}
                          </Table.Cell>

                          <Table.Cell>
                            {evaluation_session.chat_session.persona ? (
                              <Popup
                                header={
                                  evaluation_session.chat_session.persona.name
                                }
                                content={
                                  evaluation_session.chat_session.persona.persona
                                    .purpose_of_visit?.value ||
                                  evaluation_session.chat_session.persona
                                    .purpose_of_visit
                                }
                                trigger={
                                  <span>
                                    <Icon
                                      name={
                                        evaluation_session.chat_session.persona
                                          .gender === 'MALE'
                                          ? 'man'
                                          : evaluation_session.chat_session
                                              .persona.gender === 'FEMALE'
                                          ? 'woman'
                                          : 'user'
                                      }
                                    />
                                    {evaluation_session.chat_session.persona.name}
                                  </span>
                                }
                              />
                            ) : (
                              <span style={{ color: 'grey' }}>실제 인물</span>
                            )}
                          </Table.Cell>

                          <Table.Cell>
                            {evaluation_session.trainee ? (
                              <Link
                                to={`/userdashboard?userId=${evaluation_session.trainee.id}`}
                                onClick={e => e.stopPropagation()}
                              >
                                <Icon name="user circle" />
                                {evaluation_session.trainee.full_name ||
                                  evaluation_session.trainee.username}
                              </Link>
                            ) : (
                              <span style={{ color: 'grey' }}>-</span>
                            )}
                          </Table.Cell>

                          <Table.Cell>
                            {evaluation_session.trainee?.contractor ? (
                              <span>{evaluation_session.trainee.contractor.name}</span>
                            ) : (
                              <span style={{ color: 'grey' }}>-</span>
                            )}
                          </Table.Cell>

                          <Table.Cell>
                            {evaluation_session.is_postprocessed ? (
                              <Label
                                color={
                                  evaluation_session.total_score >= 80
                                    ? 'green'
                                    : evaluation_session.total_score >= 50
                                    ? 'yellow'
                                    : 'red'
                                }
                                basic
                              >
                                {evaluation_session.total_score.toFixed(0)} /{' '}
                                {sumArray(
                                  evaluation_session.evaluatedcriterion_set.map(
                                    ec => ec.criterion.score
                                  )
                                )}
                              </Label>
                            ) : (
                              <span style={{ color: 'grey' }}>-</span>
                            )}
                          </Table.Cell>

                          <Table.Cell>
                            {evaluation_session.is_postprocessed ? (
                              <Label color="green" basic>
                                <Icon name="check" /> 완료
                              </Label>
                            ) : (
                              <Label color="yellow" basic>
                                <Icon name="spinner" loading /> 처리 중
                              </Label>
                            )}
                          </Table.Cell>

                          <Table.Cell onClick={e => e.stopPropagation()}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                justifyContent: 'center',
                              }}
                            >
                              <Button.Group size="mini">
                                <Popup
                                  content="채팅 세션 보기"
                                  trigger={
                                    <Button
                                    content='세션'
                                      style={{border: '1px solid rgba(34,36,38,.15)', borderRight:'none'}}
                                      icon="comments"
                                      onClick={() =>
                                        navigate(
                                          `/chatsessionperform?chatsession_id=${evaluation_session.chat_session.id}`
                                        )
                                      }
                                    />
                                  }
                                />
                                <Popup
                                  content="상세 보고서 보기"
                                  trigger={
                                    <Button
                                    content="보고서"
                                      style={{border: '1px solid rgba(34,36,38,.15)'}}
                                      icon="file text"
                                      disabled={
                                        !evaluation_session.is_postprocessed
                                      }
                                      onClick={() =>
                                        navigate(
                                          `/evaluationsessiondetail?evaluation_session=${evaluation_session.id}`
                                        )
                                      }
                                    />
                                  }
                                />
                              </Button.Group>
                            </div>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                  </div>

                  {/* Pagination inside Left Pane */}
                  <div style={{ 
                    padding: '12px', 
                    borderTop: '1px solid rgba(0,0,0,0.05)', 
                    display: 'flex', 
                    justifyContent: 'center',
                    backgroundColor: '#f8f9fa'
                  }}>
                    <Pagination
                      activePage={page}
                      onPageChange={handlePaginationChange}
                      totalPages={Math.ceil(data.count / page_size)}
                      size="mini"
                      ellipsisItem={{ content: <Icon name="ellipsis horizontal" />, icon: true }}
                      firstItem={{ content: <Icon name="angle double left" />, icon: true }}
                      lastItem={{ content: <Icon name="angle double right" />, icon: true }}
                      prevItem={{ content: <Icon name="angle left" />, icon: true }}
                      nextItem={{ content: <Icon name="angle right" />, icon: true }}
                    />
                  </div>
                </div>
              </Grid.Column>
              {/* Right Pane: Detail */}

              <Grid.Column width={8} style={{  paddingBottom: 0 }}>
                <div
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    border: '1px solid rgba(0,0,0,0.05)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    position: 'relative',
                    overflow: 'hidden',
                  height: 'calc(100vh - 200px)',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                    }}
                  >
                    {selectedEvaluationSession ? (
                      <EvaluationTabbedResult
                        id={selectedEvaluationSession.id}
                        key={selectedEvaluationSession.id}
                        evaluationsession_pk={selectedEvaluationSession.id}
                      />
                    ) : (
                      <div
                        style={{
                          textAlign: 'center',
                          padding: '20px',
                          color: 'grey',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '100%',
                        }}
                      >
                        <Icon
                          name="hand point left outline"
                          size="huge"
                          style={{ marginBottom: '10px' }}
                        />
                        <p>
                          목록에서 평가 세션을 선택하여
                          <br />
                          상세 내용을 확인하세요.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </Grid.Column>
            </Grid>
          )}

          {/* Mobile Pagination */}
          {isMobile && (
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <Pagination
                activePage={page}
                onPageChange={handlePaginationChange}
                totalPages={Math.ceil(data.count / page_size)}
                firstItem={{
                  content: <Icon name="angle double left" />,
                  icon: true,
                }}
                lastItem={{
                  content: <Icon name="angle double right" />,
                  icon: true,
                }}
                prevItem={{ content: <Icon name="angle left" />, icon: true }}
                nextItem={{ content: <Icon name="angle right" />, icon: true }}
                ellipsisItem={{
                  content: <Icon name="ellipsis horizontal" />,
                  icon: true,
                }}
              />
            </div>
          )}
        </Container>
      </div>
    );
  }

  return content;
};

export default EvaluationSessionsList;
